import { Pipe, PipeTransform } from '@angular/core';
import { TabloStaticTextConfigOptions } from '@ui/tablo-generator/types';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { preventSingleLetterWidows } from './typography.utils';

@Pipe({
    name: 'parseStaticText',
    standalone: false
})
export class ParseStaticTextPipe implements PipeTransform {
  constructor(private domSanitizer: DomSanitizer) {}

  transform(input: string, config: TabloStaticTextConfigOptions): SafeHtml {
    const lines = input.split(/\r?\n/);
    let output: string = '';

    // Flagi stanu, informujące czy aktualnie jesteśmy wewnątrz akapitu lub listy.
    let inParagraph = false;
    let inList = false;

    for (const line of lines) {
      const trimmedLine = line;

      // 1. Obsługa pustej linii lub linii zawierającej wyłącznie białe znaki.
      if (trimmedLine === '') {
        // Jeśli napotkamy pustą linię, zamykamy otwarty akapit lub listę (jeśli takie istnieją).

        if (inParagraph) {
          output += '</div>\n';
          inParagraph = false;
        }
        if (inList) {
          output += '</ul>\n';
          inList = false;
        }
        if (!(inParagraph || inList)) {
          // Jeżeli nie mamy otwartego akapitu ani listy, dodajemy pustą linię do output.
          output += '<br>\n';
        }
        // Przechodzimy do kolejnej linii wejściowej (nie dodajemy nic do output dla pustej linii).
        continue;
      }

      // 2. Obsługa linii z nagłówkiem w formacie [Nagłówek].
      if (trimmedLine.startsWith('[') && trimmedLine.endsWith(']')) {
        // Zanim zaczniemy nowy nagłówek, upewniamy się, że zamkniemy ew. otwarty akapit lub listę.
        if (inParagraph) {
          output += '</div>\n';
          inParagraph = false;
        }
        if (inList) {
          output += '</ul>\n';
          inList = false;
        }
        // Wyodrębniamy tekst nagłówka pomiędzy nawiasami [ ].
        const headingText = preventSingleLetterWidows(
          trimmedLine.substring(1, trimmedLine.length - 1),
        );

        output += `<h1 style='
font-size:${config.headerFontSize}px;
color: ${config.headerColor};
letter-spacing: ${config.headerFontLatterSpacing}em;
text-align: ${config.headerAlign};
font-family: "${config.headerFont.fontId}", fallback;
white-space: pre-wrap;
text-transform: ${config.headerUppercase ? 'uppercase' : 'none'};
'>${headingText}</h1>\n`;
        // Przechodzimy do następnej linii (nagłówek jest samodzielnym blokiem).
        continue;
      }

      // 3. Obsługa linii zaczynającej się od '#' (element listy).
      if (trimmedLine.startsWith('#')) {
        // Jeżeli wchodzimy w listę po raz pierwszy, zamykamy otwarty akapit i otwieramy znacznik <ul>.
        if (!inList) {
          // Zamknięcie akapitu, jeśli był otwarty.
          if (inParagraph) {
            output += '</div>\n';
            inParagraph = false;
          }
          output += `<ul style='
font-size:${config.fontSize}px;
color: ${config.color};
text-align: ${config.align};
letter-spacing: ${config.fontLatterSpacing}em;
font-family: "${config.font.fontId}", fallback;
text-transform: ${config.uppercase ? 'uppercase' : 'none'};
margin-left: 0.5em'>`;
          inList = true;
        }
        // Usuwamy wszystkie początkowe znaki '#' oraz ewentualną spację po nich, aby uzyskać treść elementu listy.
        const listItemText = preventSingleLetterWidows(
          trimmedLine.replace(/^#+\s*/, ''),
        );
        output += `<li>${listItemText}</li>`;
        // (Nie dodajemy znaku nowej linii po <li>, kolejne <li> będą doklejane bezpośrednio za poprzednim.)
        continue;
      }

      // 4. Obsługa zwykłej linii tekstu (akapit).
      if (!inParagraph) {
        // Jeśli to pierwsza linia nowego akapitu, a wcześniej była otwarta lista, zamykamy tę listę.
        if (inList) {
          output += '</ul>\n';
          inList = false;
        }
        // Otwieramy nowy akapit <p>.
        output += `<div style='
font-size:${config.fontSize}px;
color: ${config.color};
text-align: ${config.align};
letter-spacing: ${config.fontLatterSpacing}em;
font-family: "${config.font.fontId}", fallback;
white-space: pre-wrap;
text-transform: ${config.uppercase ? 'uppercase' : 'none'};'>`;
        inParagraph = true;
        // Dodajemy tekst z bieżącej linii.
        output += preventSingleLetterWidows(trimmedLine);
      } else {
        // Jeżeli kontynuujemy istniejący akapit, dodajemy znacznik <br> przed nową linią tekstu.
        output += '<br>' + preventSingleLetterWidows(trimmedLine);
      }
    }

    // 5. Po przetworzeniu wszystkich linii zamykamy otwarte znaczniki akapitu lub listy (jeśli pozostały niezakończone).
    if (inParagraph) {
      output += '</div>\n';
      inParagraph = false;
    }
    if (inList) {
      output += '</ul>\n';
      inList = false;
    }

    return this.domSanitizer.bypassSecurityTrustHtml(output);
  }
}
